import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { BUSINESS, NAV, ASSETS } from "@/data/site";
import { QuoteButton } from "@/components/Buttons";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-sm" : "bg-white/60 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8 h-[112px]">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3">
            <img src={ASSETS.headerLogo} alt="Access Electrical and Air Conditioning logo" className="h-[100px] w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `relative text-[15px] font-medium transition-colors duration-200 hover:text-brand-electric ${
                    isActive ? "text-brand-electric" : "text-brand-blue"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={BUSINESS.phoneHref}
              data-testid="header-call"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-blue hover:text-brand-electric transition-colors"
            >
              <Phone className="h-4 w-4" strokeWidth={2} />
              {BUSINESS.phone}
            </a>
            <QuoteButton className="!px-6 !py-3 text-sm" testId="header-quote" />
          </div>

          <button
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full text-brand-blue"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 lg:hidden bg-brand-blue text-white pt-[112px]"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-8 gap-1">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-${item.testId}`}
                    className="block border-b border-white/10 py-4 font-display text-2xl font-medium"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                <Link
                  to="/contact"
                  data-testid="mobile-quote"
                  className="rounded-full bg-[#0B4F8A] px-6 py-4 text-center font-medium text-white"
                >
                  Get a Free Quote
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="mobile-call"
                  className="rounded-full border border-white/25 px-6 py-4 text-center font-medium"
                >
                  Call {BUSINESS.phone}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
